import _ from 'lodash';
import { CODE_POINT_PATTERN, STRUCTURAL_CAUSE_MESSAGES } from './diagnoseNameFailure';

const MAX_CODES = 50;
const MAX_FIELDS_PER_CATEGORY = 30;
const MAX_NAME_CHAR_CODES = 10;

const NAME_FIELDS = ['firstName', 'middleName', 'lastName'];

const isFilledString = (value) => typeof value === 'string' && value.trim().length > 0;

const hasValidationMessage = (value) => {
  if (isFilledString(value)) return true;

  if (Array.isArray(value)) return value.some(hasValidationMessage);

  if (value && typeof value === 'object') return Object.values(value).some(hasValidationMessage);

  return false;
};

const collectFailedFields = (formValidations) => {
  if (!formValidations || typeof formValidations !== 'object') return [];

  return Object.entries(formValidations)
    .filter(([, value]) => hasValidationMessage(value))
    .map(([key]) => key);
};

const collectPassengerFields = (passengersFormsValidation) => {
  if (!Array.isArray(passengersFormsValidation)) return { fields: [], errorCount: 0 };

  const fieldSet = new Set();
  let errorCount = 0;

  passengersFormsValidation.forEach((formValidations) => {
    const fields = collectFailedFields(formValidations);

    fields.forEach((field) => fieldSet.add(field));
    errorCount += fields.length;
  });

  return { fields: Array.from(fieldSet), errorCount };
};

// Extracts privacy-safe name diagnostics (U+XXXX codes and structural causes)
// from name-field error messages built by diagnoseNameFailure — never raw names.
const collectNameFailureDiagnostics = (passengersFormsValidation) => {
  if (!Array.isArray(passengersFormsValidation)) return { charCodes: [], structuralCauses: [] };

  const charCodes = new Set();
  const structuralCauses = new Set();

  passengersFormsValidation.forEach((formValidations) => {
    if (!formValidations || typeof formValidations !== 'object') return;

    NAME_FIELDS.forEach((field) => {
      const message = formValidations[field];
      if (!isFilledString(message)) return;

      (message.match(CODE_POINT_PATTERN) || []).forEach((code) => charCodes.add(code));
      Object.entries(STRUCTURAL_CAUSE_MESSAGES).forEach(([cause, phrase]) => {
        if (message.startsWith(`The name ${phrase}`)) structuralCauses.add(cause);
      });
    });
  });

  return {
    charCodes: Array.from(charCodes).slice(0, MAX_NAME_CHAR_CODES),
    structuralCauses: Array.from(structuralCauses),
  };
};

const collectCreditCardFields = (creditCardsFormValidations) => {
  if (!Array.isArray(creditCardsFormValidations)) return { fields: [], errorCount: 0 };

  const fieldSet = new Set();
  let errorCount = 0;

  creditCardsFormValidations.forEach((entry) => {
    const fields = collectFailedFields(entry && entry.cardformValidations);

    fields.forEach((field) => fieldSet.add(field));
    errorCount += fields.length;
  });

  return { fields: Array.from(fieldSet), errorCount };
};

const collectAdditionalFieldKeys = (invalidAdditionalCheckoutFields) => {
  if (!invalidAdditionalCheckoutFields || typeof invalidAdditionalCheckoutFields !== 'object') {
    return [];
  }

  return Object.keys(invalidAdditionalCheckoutFields).filter((key) =>
    isFilledString(invalidAdditionalCheckoutFields[key]),
  );
};

const collectEmptyPassengerFields = (emptyPassengerDetailValidation) => {
  const validations = emptyPassengerDetailValidation && emptyPassengerDetailValidation.validations;

  if (!validations || typeof validations !== 'object') return [];

  const fieldSet = new Set();

  Object.values(validations).forEach((rooms) => {
    if (!rooms || typeof rooms !== 'object') return;

    Object.values(rooms).forEach((passengerEntry) => {
      const invalidFields = passengerEntry && passengerEntry.invalidFields;

      if (!Array.isArray(invalidFields)) return;

      invalidFields.forEach((field) => {
        if (isFilledString(field)) fieldSet.add(field);
      });
    });
  });

  return Array.from(fieldSet);
};

const computeSummary = ({
  newPassengersFormsValidation,
  creditCardsFormValidations,
  similarPassengersValidation,
  emptyPassengerDetailValidation,
  isBookedTripPolicyReviewValidated,
  isBookingTripPolicyReviewValidated,
  isSavedCardValidated,
  isAgencyPlanningFeesValid,
  invalidAdditionalCheckoutFields,
  isNoteAirDeskValidated,
  isAgreedForDataCollection,
  isUserRemarksValidated,
  isCreditCardFormValid,
}) => {
  const passenger = collectPassengerFields(newPassengersFormsValidation);
  const creditCard =
    isCreditCardFormValid === true
      ? { fields: [], errorCount: 0 }
      : collectCreditCardFields(creditCardsFormValidations);
  const additionalFieldKeys = collectAdditionalFieldKeys(invalidAdditionalCheckoutFields);
  const emptyPassengerFields = collectEmptyPassengerFields(emptyPassengerDetailValidation);
  const nameDiagnostics = collectNameFailureDiagnostics(newPassengersFormsValidation);

  const hasSimilarPassengerValidation = isFilledString(similarPassengersValidation);
  const hasEmptyPassengerDetailValidation = emptyPassengerFields.length > 0;

  const isPassengersValid = passenger.errorCount === 0;
  const isCreditCardValid = creditCard.errorCount === 0 && !!isSavedCardValidated;
  const isAdditionalCheckoutFieldsValid = additionalFieldKeys.length === 0;

  const failedCategories = [];

  if (!isPassengersValid) failedCategories.push('passengers');
  if (hasEmptyPassengerDetailValidation) failedCategories.push('emptyPassengerDetail');
  if (hasSimilarPassengerValidation) failedCategories.push('similarPassenger');
  if (!isCreditCardValid) failedCategories.push('creditCard');
  if (!isAdditionalCheckoutFieldsValid) failedCategories.push('additionalFields');
  if (!isBookedTripPolicyReviewValidated) failedCategories.push('bookedTripPolicy');
  if (!isBookingTripPolicyReviewValidated) failedCategories.push('bookingTripPolicy');
  if (!isAgencyPlanningFeesValid) failedCategories.push('agencyFees');
  if (!isNoteAirDeskValidated) failedCategories.push('noteAirDesk');
  if (!isUserRemarksValidated) failedCategories.push('userRemarks');
  if (!isAgreedForDataCollection) failedCategories.push('dataCollection');

  const errorCodes = [];

  passenger.fields.forEach((field) => errorCodes.push(`passenger.${field}`));
  emptyPassengerFields.forEach((field) => errorCodes.push(`emptyPassenger.${field}`));
  creditCard.fields.forEach((field) => errorCodes.push(`creditCard.${field}`));
  additionalFieldKeys.forEach((key) => errorCodes.push(`additionalField.${key}`));

  if (hasSimilarPassengerValidation) errorCodes.push('similarPassenger.duplicate');
  if (!isBookedTripPolicyReviewValidated) errorCodes.push('policy.bookedTripReview');
  if (!isBookingTripPolicyReviewValidated) errorCodes.push('policy.bookingTripReview');
  if (!isAgencyPlanningFeesValid) errorCodes.push('agencyFees.invalid');
  if (!isNoteAirDeskValidated) errorCodes.push('noteAirDesk.missing');
  if (!isUserRemarksValidated) errorCodes.push('userRemarks.invalid');
  if (!isAgreedForDataCollection) errorCodes.push('dataCollection.notAgreed');
  if (!isSavedCardValidated) errorCodes.push('creditCard.savedCard');

  const errorCount =
    passenger.errorCount +
    creditCard.errorCount +
    additionalFieldKeys.length +
    emptyPassengerFields.length +
    (hasSimilarPassengerValidation ? 1 : 0) +
    (!isBookedTripPolicyReviewValidated ? 1 : 0) +
    (!isBookingTripPolicyReviewValidated ? 1 : 0) +
    (!isAgencyPlanningFeesValid ? 1 : 0) +
    (!isNoteAirDeskValidated ? 1 : 0) +
    (!isUserRemarksValidated ? 1 : 0) +
    (!isAgreedForDataCollection ? 1 : 0) +
    (!isSavedCardValidated ? 1 : 0);

  return compact({
    errorCount,
    failedCategories,
    primaryFailedCategory: failedCategories[0] || null,
    errorCodes: _.uniq(errorCodes).slice(0, MAX_CODES),
    passengerErrorCount: passenger.errorCount,
    creditCardErrorCount: creditCard.errorCount,
    additionalFieldsErrorCount: additionalFieldKeys.length,
    invalidNameCharCodes: nameDiagnostics.charCodes,
    nameStructuralCauses: nameDiagnostics.structuralCauses,
    failedPassengerFields: passenger.fields.slice(0, MAX_FIELDS_PER_CATEGORY),
    failedCreditCardFields: creditCard.fields.slice(0, MAX_FIELDS_PER_CATEGORY),
    failedAdditionalFieldKeys: additionalFieldKeys.slice(0, MAX_FIELDS_PER_CATEGORY),
    failedEmptyPassengerFields: emptyPassengerFields.slice(0, MAX_FIELDS_PER_CATEGORY),
    isPassengersValid,
    isCreditCardValid,
    isAdditionalCheckoutFieldsValid,
    hasSimilarPassengerValidation,
    hasEmptyPassengerDetailValidation,
  });
};

const compact = (summary) => {
  const result = {};

  Object.entries(summary).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value) && value.length === 0) return;
    if (typeof value === 'number' && value === 0) return;

    result[key] = value;
  });

  return result;
};

const buildValidationErrorSummary = (input) => {
  try {
    return computeSummary(input || {});
  } catch (error) {
    if (typeof console !== 'undefined' && console.error) {
      console.error('buildValidationErrorSummary failed', error);
    }
    return null;
  }
};

export default buildValidationErrorSummary;
